import { config } from '@psl/functions/config';
import { useContext, useEffect } from 'react';
import { BehaviorSubject, defer, from } from 'rxjs';
import {
  Statsig,
  StatsigContext,
  StatsigUser,
} from 'statsig-react-native-expo';
import { OnboardingStateManager } from '../../flows/user/functions/onboardingState';
import AsyncStorage, { Key } from '../../utils/asyncStorage';
import { makeDebug } from '../../utils/makeDebug';
import { shareReplayWithDelay } from '../../utils/rxExtensions';
import { useCurrentUserProfile } from '../auth/utils';
import { kPrivateEmailPlaceholder } from '../constants';
import { useObservable } from '../hooks/useObservable';
import { isSeller } from '../models/profiles';

const debug = makeDebug('statsig');

export enum StatsigState {
  Loading = 'Loading',
  InitializationFinish = 'InitializationFinish',
  UpdateUserFinish = 'UpdateUserFinish',
}

export const StatsigState$ = new BehaviorSubject(StatsigState.Loading);

export const useStatsigState = () => {
  return useObservable(StatsigState$, 'statsig-state', StatsigState.Loading);
};

const randomUserInLocalStorage = async (): Promise<StatsigUser> => {
  const result = await AsyncStorage.getJSONData<StatsigUser>(
    Key.statsigInstallationUser,
  );
  if (result != null) {
    return result;
  }
  const user = {
    email: `${Math.random()}@anonym.popshop.live`,
    appVersion: config.version,
    custom: {
      createdAt: new Date().toISOString(),
    },
  };
  await AsyncStorage.storeJSONData(Key.statsigInstallationUser, user);
  return user;
};

export const initStatsigUser$ = defer(() =>
  from(randomUserInLocalStorage()),
).pipe(
  shareReplayWithDelay(
    {
      bufferSize: 1,
    },
    100 * 1000,
  ),
);

export const useInitStatsigUser = () =>
  useObservable(initStatsigUser$, 'init-statsig-user');

export const StatsigUser$ = new BehaviorSubject<StatsigUser>({});

export const useStatsigUser = () => {
  return useObservable(StatsigUser$, 'statsig-user');
};

export function useStatsig() {
  const profile = useCurrentUserProfile();
  const statsigStore = useContext(StatsigContext);
  const initStatsigUser = useInitStatsigUser();

  useEffect(() => {
    if (profile == null) {
      debug('profile is null, try to initialize statsig user');
      OnboardingStateManager.shared.statsigReadyForUserWithProfile(false);
      void (async () => {
        if (initStatsigUser == null) {
          debug('initStatsigUser is null, return');
          return;
        }
        debug(
          'initStatsigUser is not null, update statsig user object to: ',
          initStatsigUser,
        );
        StatsigUser$.next(initStatsigUser);
        await Statsig.updateUser(initStatsigUser);
        StatsigState$.next(StatsigState.InitializationFinish);
      })();
      return;
    }

    if (StatsigUser$.getValue().userID === profile.id) {
      debug('profile is not null, but equals to existing user, skip');
      return;
    }

    void (async () => {
      const user: StatsigUser = {
        userID: profile.id,
        email: kPrivateEmailPlaceholder,
        appVersion: config.version,
        custom: {
          userID: profile.id,
          isSeller: isSeller(profile),
          createdAt: profile.createdAt?.toDate().toISOString(),
          updatedAt: profile.updatedAt?.toDate().toISOString(),
        },
      };
      debug('profile is not null, update statsig user object to: ', user);
      StatsigUser$.next(user);
      await Statsig.updateUser(user);
      OnboardingStateManager.shared.statsigReadyForUserWithProfile(true);
      StatsigState$.next(StatsigState.UpdateUserFinish);
    })();
  }, [initStatsigUser, profile, statsigStore]);
}
